import React from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  ArrowUpRight,
} from "lucide-react";
import { SOCIAL_ICONS } from "@/components/lumiqo/SocialIcons";
import { BUSINESS, SOCIAL_LINKS } from "@/lib/lumiqoData";

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
  <a
    href={href}
    className="group flex items-start gap-4 py-4 border-b border-white/10 last:border-0 transition-colors"
    data-testid={testid}
  >
    <span className="shrink-0 w-10 h-10 rounded-full bg-white/5 group-hover:bg-[#1d72ff] border border-white/10 group-hover:border-[#1d72ff] flex items-center justify-center transition-all duration-300">
      <Icon className="w-4 h-4 text-white" />
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-semibold">
        {label}
      </div>
      <div className="mt-1 text-white text-base sm:text-lg font-display font-semibold tracking-tight group-hover:text-[#4d8eff] transition-colors break-words">
        {value}
      </div>
    </div>
    <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#4d8eff] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300 mt-3" />
  </a>
);

const About = () => {
  return (
    <section
      id="about"
      className="relative py-24 sm:py-32 overflow-hidden bg-[#0a1d4d] text-white"
      data-testid="about-section"
    >
      <div className="absolute inset-0 lq-grid-bg-dark opacity-30 [mask-image:radial-gradient(circle_at_center,black,transparent_70%)]" />
      <div
        className="lq-orb"
        style={{
          background: "#1d72ff",
          width: 520,
          height: 520,
          top: -160,
          left: -160,
          opacity: 0.28,
        }}
      />
      <div
        className="lq-orb"
        style={{
          background: "#1d72ff",
          width: 420,
          height: 420,
          bottom: -180,
          right: -120,
          opacity: 0.2,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-20">
          {/* Left — Brand */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-block px-3 py-1 rounded-full bg-white/8 border border-white/12 text-white/85 text-xs font-semibold tracking-wide uppercase">
              About
            </div>

            <h2
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.04] tracking-tight"
              data-testid="about-tagline"
            >
              {BUSINESS.tagline}
            </h2>

            <p
              className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl"
              data-testid="about-description"
            >
              {BUSINESS.about}
            </p>
          </motion.div>

          {/* Right — Contact card */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div
              className="lq-glass-dark rounded-3xl p-7 sm:p-9 border border-white/12 lq-shadow-soft"
              id="contact"
              data-testid="about-contact-card"
            >
              <div className="font-display font-bold text-xl sm:text-2xl tracking-tight">
                Get in touch
              </div>
              <p className="mt-2 text-white/65 text-sm leading-relaxed">
                Pitch your brand, ask a question or say hi. We reply within
                24&nbsp;hours.
              </p>

              <div className="mt-6">
                <ContactRow
                  icon={Mail}
                  label="Email"
                  value={BUSINESS.email}
                  href={`mailto:${BUSINESS.email}`}
                  testid="about-email"
                />
                <ContactRow
                  icon={Phone}
                  label="Phone"
                  value={BUSINESS.phone}
                  href={`tel:${BUSINESS.phoneRaw}`}
                  testid="about-phone"
                />
              </div>

              <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-semibold">
                  Follow
                </div>
                <div className="flex gap-2.5">
                  {SOCIAL_LINKS.map(({ label, href, icon }) => {
                    const Icon = SOCIAL_ICONS[icon];
                    return (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#1d72ff] border border-white/10 hover:border-[#1d72ff] flex items-center justify-center transition-colors duration-300"
                        data-testid={`about-social-${label.toLowerCase()}`}
                      >
                        <Icon className="w-4 h-4 text-white" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom strip — copyright */}
        <div className="mt-16 pt-6 border-t border-white/10 flex items-center justify-center">
          <p className="text-white/45 text-xs sm:text-sm text-center">
            © 2025 {BUSINESS.name}. All rights reserved.
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;
